import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useNavigate,
  useLocation,
  useMatch,
  Navigate,
  NavigateOptions,
} from 'react-router-dom';
import { RoutingStep } from '../../router/models/Routes';
import localStorageService, {
  LOCAL_STORAGE_KEYS,
} from '../../services/localStorageService';
import StepProviderContext, {
  renderAsLinks,
  StepPath,
  StepsContextProps,
} from './Steps.context';
import useEffectSkipFirst from '../../hooks/useEffectSkipFirst';
import { ListingRoute } from 'router/subrouters/ListingCreationWizard/models';
import { LISTING_PACKAGE } from 'models/Listing';
import { isUndefined } from 'utils';
import { EDIT_ROUTE } from 'data/global';

export const MEDIA_PACKAGE_PATH = 'media-package';

export const PACKAGES_WITHOUT_MEDIA: string[] = [
  LISTING_PACKAGE.SILVER,
  LISTING_PACKAGE.GOLD,
  LISTING_PACKAGE.COMPREHENSIVE,
];

type StepsProps = {
  storageKeySuffix: string;
  steps?: Array<ListingRoute>;
  children?: React.ReactNode;
  useDraftButtons?: boolean;
  blockOtherStepsWhenCurrentIsInvalid?: boolean;
  completedSteps?: number;
};

export type StepsRef = StepsContextProps;

const StepsProvider: React.ForwardRefRenderFunction<StepsRef, StepsProps> = (
  props,
  ref,
) => {
  const {
    steps: pSteps = [],
    storageKeySuffix,
    useDraftButtons = false,
    children,
    blockOtherStepsWhenCurrentIsInvalid = false,
    completedSteps: completedStepsProps,
  } = props;

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = useMatch(EDIT_ROUTE);
  const [finishSubmit, setFinishSubmit] = useState();
  const [editedSteps, setEditedSteps] = useState<Record<string, boolean>>({});

  const initCompletedFlagsForEdit = useCallback(
    (steps: Array<RoutingStep>) =>
      steps.map((step) => ({ ...step, isStepValid: true })),
    [],
  );

  const [steps, setSteps] = useState<Array<RoutingStep>>(
    isEdit
      ? initCompletedFlagsForEdit(pSteps as Array<RoutingStep>)
      : (pSteps as Array<RoutingStep>),
  );

  const key = useMemo(
    () => [LOCAL_STORAGE_KEYS.COMPLETED_STEPS, storageKeySuffix].join('-'),
    [storageKeySuffix],
  );

  const [completedStepsState, setCompletedSteps] = useState(
    +localStorageService.getItem(key) || 0,
  );

  const completedSteps = useMemo(
    () =>
      !isUndefined(completedStepsProps)
        ? completedStepsProps
        : completedStepsState,
    [completedStepsProps, completedStepsState],
  );

  const stepperLength = useMemo(() => steps.length, [steps.length]);

  const useLinks = useMemo(() => renderAsLinks(steps), [steps]);

  const routingStepIndex = useMemo(
    () =>
      renderAsLinks(steps)
        ? steps.findIndex((step) =>
            pathname.split('/').some((part) => part === step.path),
          )
        : null,
    [pathname, steps],
  );

  const [currentStep, setCurrentStep] = useState(
    useLinks ? routingStepIndex : completedSteps,
  );

  const nextStep = useCallback(
    (navigateOptions?: NavigateOptions) => {
      if (useLinks) {
        if (currentStep < stepperLength - 1)
          navigate(steps[currentStep + 1]?.path, navigateOptions);
      } else
        setCurrentStep((oldStep) => Math.min(oldStep + 1, stepperLength - 1));
    },
    [useLinks, currentStep, stepperLength, navigate, steps],
  );

  const prevStep = useCallback(
    (navigateOptions?: NavigateOptions) => {
      if (useLinks) {
        if (currentStep > 0) {
          navigate(steps[currentStep - 1].path, navigateOptions);
        }
      } else {
        setCurrentStep((oldStep) => Math.max(oldStep - 1, 0));
      }
    },
    [useLinks, currentStep, navigate, steps],
  );

  const goToStep = useCallback(
    (step: number, navigateOptions?: NavigateOptions) => {
      if (useLinks) {
        if (step <= stepperLength - 1)
          navigate(steps[step]?.path, navigateOptions);
      } else {
        if (step <= stepperLength - 1) setCurrentStep(step);
        else
          setCurrentStep((oldStep) => Math.min(oldStep + 1, stepperLength - 1));
      }
    },
    [useLinks, stepperLength, navigate, steps],
  );

  const stepLabels = useMemo(
    () =>
      renderAsLinks(steps) ? steps.map((route) => t(route.labelId)) : steps,
    [steps, t],
  );

  const linkSteps = useMemo(
    () =>
      renderAsLinks(steps)
        ? steps.map(
            (route) =>
              ({
                label: t(route.labelId),
                path: route.path,
                isStepValid: route.isStepValid,
              } as StepPath),
          )
        : null,
    [steps, t],
  );

  const removeCompletedStepsFromStorage = useCallback(() => {
    localStorageService.removeItem(key);
  }, [key]);

  const activeStep = useMemo(() => steps[currentStep], [steps, currentStep]);

  const providedValues = useMemo(
    () =>
      ({
        currentStep,
        steps,
        stepLabels,
        linkSteps,
        renderAsLinks: useLinks,
        completedSteps,
        storageKeySuffix,
        useDraftButtons,
        blockOtherStepsWhenCurrentIsInvalid,
        setSteps,
        removeCompletedStepsFromStorage,
        setCompletedSteps,
        setCurrentStep,
        prevStep,
        nextStep,
        goToStep,
        finishSubmit,
        setFinishSubmit,
        activeStep,
        editedSteps,
        setEditedSteps,
      } as StepsRef),
    [
      currentStep,
      steps,
      linkSteps,
      stepLabels,
      useLinks,
      completedSteps,
      storageKeySuffix,
      useDraftButtons,
      blockOtherStepsWhenCurrentIsInvalid,
      setSteps,
      removeCompletedStepsFromStorage,
      setCompletedSteps,
      nextStep,
      prevStep,
      goToStep,
      finishSubmit,
      setFinishSubmit,
      activeStep,
      editedSteps,
      setEditedSteps,
    ],
  );

  useImperativeHandle(ref, () => providedValues, [providedValues]);

  useEffectSkipFirst(() => {
    if (useLinks && routingStepIndex >= 0) {
      setCurrentStep(routingStepIndex);
      setCompletedSteps((old) => Math.max(old, routingStepIndex));
    }
  }, [routingStepIndex, useLinks]);

  useEffectSkipFirst(() => {
    if (!useLinks) setCompletedSteps((old) => Math.max(old, currentStep));
  }, [useLinks, currentStep]);

  useEffectSkipFirst(() => {
    localStorageService.setItem(key, String(completedSteps));
  }, [completedSteps]);

  useEffect(() => {
    if (useLinks) return;

    setCurrentStep((old) => Math.min(completedSteps, old));
  }, [completedSteps, useLinks]);

  useEffect(() => {
    if (isEdit) {
      setCompletedSteps(steps.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      removeCompletedStepsFromStorage();
    };
  }, [removeCompletedStepsFromStorage]);

  if (useLinks && completedSteps < currentStep) {
    return <Navigate to={steps[completedSteps].path} />;
  }

  return (
    <StepProviderContext.Provider value={providedValues}>
      {children}
    </StepProviderContext.Provider>
  );
};

export default forwardRef(StepsProvider);
